import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Power, PowerOff, Pencil, Trash2, Plus, X, Check, AlertTriangle, CalendarOff } from 'lucide-react';
import Navbar from "../components/Navbar";
import ProviderTabs from "../components/ProviderTabs";
import ProviderLoadingScreen from "../components/ProviderLoadingScreen";
import { formatDOP } from '../lib/currency';
import { ZONES } from '../lib/zones';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderAssets() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState(null);

  const TYPES = [
    { value: 'jet_ski', label: t('homepage.categoryJet') },
    { value: 'atv', label: t('homepage.categoryAtv') },
    { value: 'boat', label: t('homepage.categoryBoat') },
    { value: 'motorcycle', label: t('homepage.categoryMoto') },
    { value: 'scooter', label: t('homepage.categoryScooter') },
  ];

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchAssets = useCallback(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/provider/assets`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => { setAssets(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => navigate('/login'));
  }, [navigate]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const toggleActive = async (assetId, isActive) => {
    await fetch(`${BACKEND_URL}/api/provider/assets/${assetId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_active: !isActive })
    });
    setAssets(prev => prev.map(a => a.asset_id === assetId ? { ...a, is_active: !isActive } : a));
  };

  const openEdit = (asset) => {
    setEditingAsset(asset.asset_id);
    setEditForm({
      name: asset.name,
      description: asset.description,
      price_per_hour: asset.price_per_hour || '',
      price_per_day: asset.price_per_day,
      location_zone: asset.location_zone,
      pickup_address: asset.pickup_address || '',
      type: asset.type,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/assets/${editingAsset}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editForm.name,
          description: editForm.description,
          price_per_hour: editForm.price_per_hour ? Math.round(Number(editForm.price_per_hour)) : null,
          price_per_day: Math.round(Number(editForm.price_per_day)),
          location_zone: editForm.location_zone,
          pickup_address: editForm.pickup_address,
        })
      });
      if (res.ok) {
        showToast('success', t('providerAssets.updated'));
        setEditingAsset(null);
        fetchAssets();
      } else {
        showToast('error', t('providerAssets.saveErrorGeneric'));
      }
    } catch {
      showToast('error', t('clientDashboard.connectionError'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (assetId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/provider/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        showToast('success', t('providerAssets.deleted'));
        setDeletingId(null);
        fetchAssets();
      } else {
        showToast('error', t('providerAssets.deleteFail'));
        setDeletingId(null);
      }
    } catch {
      showToast('error', t('clientDashboard.connectionError'));
      setDeletingId(null);
    }
  };

  const typeLabel = (type) => TYPES.find(t => t.value === type)?.label || type;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <ProviderTabs />

      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-2xl shadow-lg text-white font-medium text-sm transition-all
          ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <button onClick={() => navigate('/provider/dashboard')} className="mb-2 flex items-center gap-2 text-gray-500 hover:text-[#134168] text-sm font-medium transition-colors">
              {t('providerAssets.backToPanel')}
            </button>
            <h1 className="text-3xl md:text-4xl font-heading font-bold">{t('providerAssets.title')}</h1>
          </div>
          <button
            onClick={() => navigate('/provider/post-asset')}
            className="flex items-center gap-2 bg-[#134168] text-white px-5 py-3 rounded-full font-bold hover:bg-[#39b4aa] transition-all"
          >
            <Plus className="w-5 h-5" /> {t('providerAssets.publishNew')}
          </button>
        </div>

        {loading ? (
          <ProviderLoadingScreen chrome={false} className="py-20" message={t('providerAssets.loading', 'Cargando tus vehículos...')} />
        ) : assets.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 shadow-sm text-center">
            <p className="text-gray-500 mb-4">{t('providerAssets.noAssetsYet')}</p>
            <button onClick={() => navigate('/provider/post-asset')} className="bg-[#134168] text-white px-6 py-3 rounded-full font-bold hover:bg-[#39b4aa] transition-all">
              {t('providerAssets.publishFirst')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assets.filter(a => !a.name?.startsWith('[ELIMINADO]')).map(asset => (
              <div key={asset.asset_id} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-200 flex flex-col">
                <div className="relative">
                  <img
                    src={asset.images?.[0]?.url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`}
                    alt={asset.name}
                    className="w-full h-48 object-cover"
                  />
                  {asset.moderation_status === 'pending' ? (
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold bg-amber-500 text-white">
                      ⏳ {t('providerAssets.pendingReview', 'En revisión')}
                    </span>
                  ) : asset.moderation_status === 'rejected' ? (
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
                      {t('providerAssets.rejected', 'Rechazado')}
                    </span>
                  ) : (
                    <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${
                      asset.is_active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                    }`}>
                      {asset.is_active ? t('providerAssets.active') : t('providerAssets.inactive')}
                    </span>
                  )}
                </div>

                <div className="p-5 flex flex-col flex-1">
                  <div className="mb-1">
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">{typeLabel(asset.type)}</span>
                  </div>
                  <h3 className="font-bold text-lg mb-1 leading-tight">{asset.name}</h3>
                  {asset.moderation_status === 'rejected' && asset.moderation_rejection_reason && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-2">
                      <strong>{t('providerAssets.rejectedReasonLabel', 'Motivo:')}</strong> {asset.moderation_rejection_reason}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mb-1">📍 {asset.location_zone}</p>
                  <div className="flex gap-3 mb-4">
                    <span className="text-[#134168] font-bold">{formatDOP(asset.price_per_day)}{t('providerAssets.perDayMini')}</span>
                    {asset.price_per_hour && <span className="text-gray-500 text-sm">{formatDOP(asset.price_per_hour)}{t('providerAssets.perHourMini')}</span>}
                  </div>

                  {asset.rating > 0 && (
                    <p className="text-sm text-yellow-600 mb-3">{t('providerAssets.rateAvg', { r: asset.rating?.toFixed(1), c: asset.total_rentals })}</p>
                  )}

                  <div className="flex gap-2 mt-auto pt-3 border-t border-gray-100">
                    <button
                      onClick={() => toggleActive(asset.asset_id, asset.is_active)}
                      title={asset.is_active ? t('providerAssets.inactive') : t('providerAssets.active')}
                      className={`p-2 rounded-xl transition ${asset.is_active
                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    >
                      {asset.is_active ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}
                    </button>

                    <button
                      onClick={() => navigate(`/provider/assets/${asset.asset_id}/availability`)}
                      title={t('providerAssets.availability', 'Disponibilidad')}
                      className="p-2 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
                    >
                      <CalendarOff className="w-5 h-5" />
                    </button>

                    <button
                      onClick={() => openEdit(asset)}
                      title={t('providerAssets.edit')}
                      className="flex-1 flex items-center justify-center gap-2 border border-[#134168] text-[#134168] py-2 rounded-xl font-medium hover:bg-blue-50 transition text-sm"
                    >
                      <Pencil className="w-4 h-4" /> {t('providerAssets.edit')}
                    </button>

                    <button
                      onClick={() => setDeletingId(asset.asset_id)}
                      title={t('providerAssets.delete')}
                      className="p-2 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingAsset && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">{t('providerAssets.editAsset')}</h2>
              <button onClick={() => setEditingAsset(null)} className="p-2 rounded-xl hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('providerAssets.namelabel')}</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('providerAssets.descriptionLabel')}</label>
                <textarea
                  value={editForm.description}
                  onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('providerAssets.pricePerHour')}</label>
                  <input
                    type="number" step="1" min="1"
                    value={editForm.price_per_hour}
                    onChange={e => setEditForm({ ...editForm, price_per_hour: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('providerAssets.pricePerDay')}</label>
                  <input
                    type="number" step="1" min="1" required
                    value={editForm.price_per_day}
                    onChange={e => setEditForm({ ...editForm, price_per_day: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('providerAssets.zoneLabel')}</label>
                <select
                  value={editForm.location_zone}
                  onChange={e => setEditForm({ ...editForm, location_zone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                >
                  {ZONES.map(z => <option key={z}>{z}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('providerAssets.pickupAddress')}</label>
                <input
                  type="text"
                  value={editForm.pickup_address}
                  onChange={e => setEditForm({ ...editForm, pickup_address: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#134168]"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingAsset(null)}
                className="flex-1 border border-gray-200 py-3 rounded-full font-bold text-gray-600 hover:bg-gray-50 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="flex-1 bg-[#134168] text-white py-3 rounded-full font-bold hover:bg-[#39b4aa] transition disabled:opacity-50"
              >
                {saving ? t('providerAssets.savingState') : t('providerAssets.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingId && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('providerAssets.deleteConfirmTitle', '¿Ocultar vehículo?')}</h3>
            <p className="text-gray-500 text-sm mb-6">{t('providerAssets.deleteConfirmDesc', 'Esta acción desactivará y ocultará permanentemente el vehículo de los resultados de búsqueda. El historial de reservas se mantendrá intacto.')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletingId(null)}
                className="flex-1 border border-gray-200 py-3 rounded-full font-bold text-gray-600 hover:bg-gray-50 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => confirmDelete(deletingId)}
                className="flex-1 bg-red-500 text-white py-3 rounded-full font-bold hover:bg-red-600 transition"
              >
                {t('providerAssets.delete', 'Ocultar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
